'use client'

import { useState, useRef, useEffect } from 'react'
import { useRealtimeChat, ChatMessage } from '../../hooks/use-realtime-chat'
import { ChatMessageItem } from './chat-message'
import { Send, MessageSquare, X } from 'lucide-react'
import { cn } from '../../lib/utils'

interface ChatWidgetProps {
  userId: string
  username: string
  userAvatar?: string | null
  isAdmin?: boolean
  embedded?: boolean    // True khi dùng trong Admin Dashboard
  isShowHeader?: boolean
  className?: string
}

export function ChatWidget({ userId, username, userAvatar, isAdmin = false, embedded = false, isShowHeader = true, className }: ChatWidgetProps) {
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(embedded) // Auto open nếu embedded
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const isInitialLoadRef = useRef(true) // Track nếu là lần đầu load conversation
  const { messages, sendMessage, isConnected, isLoading, unreadCount, markAsRead } = useRealtimeChat({
    userId,
    userName: username,
    userAvatar,
    isAdmin
  })

  // Handle open chat - mark as read
  const handleOpenChat = () => {
    setIsOpen(true)
    if (!isAdmin) {
      markAsRead() // Reset unread count
    }
  }

  const scrollToBottom = (instant = false) => {
    if (instant && messagesContainerRef.current) {
      // Scroll ngay lập tức bằng cách set scrollTop trực tiếp
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    } else {
      // Scroll mượt mà
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Reset flag khi userId thay đổi (admin chuyển conversation)
  useEffect(() => {
    if (isAdmin && embedded) {
      isInitialLoadRef.current = true

      // Force scroll bằng interval - scroll liên tục cho đến khi đúng vị trí
      const scrollToBottomForce = () => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current
          container.scrollTop = container.scrollHeight
        }
      }

      // Scroll ngay lập tức
      scrollToBottomForce()

      // Scroll liên tục mỗi 50ms trong 1 giây đầu
      const interval = setInterval(scrollToBottomForce, 50)

      // Dừng interval sau 1 giây
      const stopTimer = setTimeout(() => {
        clearInterval(interval)
      }, 1000)

      return () => {
        clearInterval(interval)
        clearTimeout(stopTimer)
      }
    }
  }, [userId, isAdmin, embedded])

  // Scroll to bottom khi messages thay đổi
  useEffect(() => {
    if (messages.length > 0) {
      // Nếu là lần đầu load hoặc admin đang ở embedded mode và vừa chuyển conversation
      if (isInitialLoadRef.current && isAdmin && embedded) {
        // Scroll instant cho lần đầu load conversation
        const scrollNow = () => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
          }
        }

        requestAnimationFrame(() => {
          scrollNow()
          setTimeout(scrollNow, 50)
          setTimeout(() => {
            scrollNow()
            isInitialLoadRef.current = false
          }, 150)
        })
      } else {
        // Scroll smooth cho tin nhắn mới
        scrollToBottom()
      }
    }
  }, [messages, isAdmin, embedded])

  // Scroll lại khi loading xong (đảm bảo messages đã được render)
  useEffect(() => {
    if (!isLoading && messages.length > 0 && isAdmin && embedded) {
      // Force scroll khi loading xong
      const scrollNow = () => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
        }
      }

      requestAnimationFrame(scrollNow)
      setTimeout(scrollNow, 100)
      setTimeout(scrollNow, 300)
    }
  }, [isLoading, isAdmin, embedded])

  // Scroll to bottom instant khi mở widget chat
  useEffect(() => {
    if (isOpen) {
      // Sử dụng setTimeout để đảm bảo DOM đã render xong
      setTimeout(() => {
        scrollToBottom(true) // instant scroll
      }, 50)
    }
  }, [isOpen])

  // Mark as read when chat is open (for embedded mode)
  useEffect(() => {
    if (isOpen && !isAdmin) {
      markAsRead()
    }
  }, [isOpen, isAdmin, markAsRead])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim() || !isConnected) return

    await sendMessage(inputValue)
    setInputValue('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  // Nếu embedded mode, không hiển thị button bubble
  if (!isOpen && !embedded) {
    return (
      <button
        style={{ backgroundColor: "#0A3170", position: 'relative' }}
        onClick={handleOpenChat}
        className={cn(
          'fixed bottom-20 left-6 p-4 bg-primary text-white rounded-full shadow-lg hover:scale-110 transition-transform',
          className
        )}
      >
        <MessageSquare size={24} />
        {/* Badge hiển thị số tin nhắn chưa đọc */}
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              backgroundColor: '#ff4444',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    )
  }

  // Nếu embedded=false và !isOpen, không render gì
  if (!isOpen) {
    return null
  }

  return (
    <div
      style={{ zIndex: 999 }}
      className={cn(
        embedded
          ? 'h-full w-full bg-white flex flex-col'
          : 'fixed bottom-6 left-6 w-96 h-[500px] border border-border rounded-lg shadow-2xl flex flex-col',
        className
      )}
    >
      {/* Header */}
      {/* <div style={{ backgroundColor: "#05224e" }} className="flex items-center justify-between p-4 border-b border-border text-primary-foreground rounded-t-lg">
        <div className="flex items-center gap-3">
          <MessageSquare color="white" size={20} />
          <div>
            <h3 className="font-semibold text-sm text-white">
              {isAdmin ? 'Hỗ trợ' : 'Chat với chúng tôi'}
            </h3>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-2 h-2 rounded-full',
                  isConnected ? 'bg-green-400' : 'bg-red-400'
                )}
              />
              <span className="text-xs opacity-90 text-white">
                {isConnected ? 'Connected' : 'Disconnecting...'}
              </span>
            </div>
          </div>
        </div>
        {!embedded && (
          <button
            onClick={() => setIsOpen(false)}
            className="hover:bg-primary-foreground/10 p-1 rounded-lg transition-colors text-white"
          >
            <X size={20} />
          </button>
        )}
      </div> */}

      {isShowHeader && (
        <div style={{ backgroundColor: "#05224e" }} className="flex items-center justify-between p-4 border-b border-border text-primary-foreground rounded-t-lg">
          <div className="flex items-center gap-3">
            <MessageSquare color="white" size={20} />
            <div>
              <h3 className="font-semibold text-sm text-white">
                {isAdmin ? 'Hỗ trợ' : 'Chat với chúng tôi'}
              </h3>
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    'w-2 h-2 rounded-full',
                    isConnected ? 'bg-green-400' : 'bg-red-400'
                  )}
                />
                <span className="text-xs opacity-90 text-white">
                  {isConnected ? 'Connected' : 'Disconnecting...'}
                </span>
              </div>
            </div>
          </div>
          {!embedded && (
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-primary-foreground/10 p-1 rounded-lg transition-colors text-white"
            >
              <X size={20} />
            </button>
          )}
        </div>
      )}

      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-2" style={{ backgroundColor: "#FDFDFD" }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Chưa có tin nhắn nào. Hãy bắt đầu cuộc trò chuyện!
          </div>
        ) : (
          messages.map((message: ChatMessage, index: number) => {
            // Tin nhắn của mình nếu:
            // - User thường: tin nhắn KHÔNG từ admin (is_from_admin = false)
            // - SuperAdmin: tin nhắn TỪ admin (is_from_admin = true)
            const isOwnMessage = isAdmin ? message.isFromAdmin : !message.isFromAdmin
            const prevMessage = messages[index - 1]
            const showHeader = !prevMessage ||
              prevMessage.isFromAdmin !== message.isFromAdmin

            return (
              <ChatMessageItem
                key={message.id}
                message={message}
                isOwnMessage={isOwnMessage}
                showHeader={showHeader}
              />
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form style={{ backgroundColor: "#FDFDFD", zIndex: 1000 }} onSubmit={handleSubmit} className="p-4 border-t border-border">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isConnected ? 'Type a message...' : 'Connecting...'}
            disabled={!isConnected}
            className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || !isConnected}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            <Send size={20} />
          </button>
        </div>
      </form>
    </div>
  )
}
